'use client'

import React, { FC, ReactElement } from 'react'
import axios from 'axios'
import MarketIcon from '@/icons/market'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Loading from '@/components/circle-loading'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import { API_URL } from '@/utils/constants'
import styles from './InscriptionActionSell.module.css'
import ActionDrawer from '@/components/action-drawer'
import ExchangeRateInput from '@/components/exchange-rate-input'
import { Inscription } from '@/utils/types/inscription'
import InscriptionWarningMultiInscriptions from '@/components/inscription-warning/inscription-warning-multi-inscriptions'
import InscriptionWarningExcessSats from '@/components/inscription-warning/inscription-warning-excess-sats'
import InscriptionWarningBelowFloor from '@/components/inscription-warning/inscription-warning-below-floor'
import ErrorHandler from '@/utils/error-handler'
import { protocolFromInscription } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import SnipeProtectionRow from '@/components/snipe-protection-toggle'
import { prepareSecureListing } from '@/utils/market/secure-listing-client'
import { lowerQuote, useMark } from '@/lib/quotes/hooks'

const EVENT = 'open-sell-modal'

export type ForceParams = {
	forceExcessSats?: boolean
	forceMultiInscriptions?: boolean
	forceBelowFloor?: boolean
}

const InscriptionActionSell: FC<{
	open: boolean
	setOpen: (v: boolean) => void
	inscription: Inscription
	className?: string
	buttonColor?: 'gray' | 'black'
}> = (props): ReactElement => {
	const { open, setOpen, inscription } = props

	const snipeProtection = useSnipeProtection(
		protocolFromInscription(inscription),
		(inscription as any)?.collection?.slug ?? (inscription as any)?.collection_slug ?? null
	)
	const [value, setValue] = React.useState('')
	const [bitcoinAmount, setBitcoinAmount] = React.useState<number>((null as any))
	const [loading, setLoading] = React.useState(false)
	const handleDrawerToggle = React.useCallback(() => setOpen(!open), [open, setOpen])
	const [forceParams, setForceParams] = React.useState<ForceParams>()
	const [openExcessSats, setOpenExcessSats] = React.useState(false)
	const [totalSats, setTotalSats] = React.useState<number>()
	const [openMultiInscriptions, setOpenMultiInscriptions] = React.useState(false)
	const [inscriptions, setInscriptions] = React.useState<Inscription[]>()
	const [openBelowFloor, setOpenBelowFloor] = React.useState(false)
	const [floorPrice, setFloorPrice] = React.useState<number>()

	// The suggested price: what the piece is worth -- fair value or floor,
	// whichever is lower -- the same rule every other listing flow and the
	// wallet valuation use.
	const slug = (inscription as any)?.collection?.slug ?? null
	const mark = useMark(slug)
	const worthSats = lowerQuote(mark?.fair_sats, Number((inscription as any)?.collection?.floor_price) || null)
	React.useEffect(() => {
		if (open && !value && worthSats) setValue(String(worthSats / 1e8))
		// Only when the drawer opens or the worth first arrives.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, worthSats])

	const handleSubmit = React.useCallback(
		async (evt?: any, params?: ForceParams) => {
			evt?.preventDefault()
			evt?.stopPropagation()

			if (loading || value?.length === 0) {
				return
			}
			setLoading(true)

			let creator_address = 0

			const salePrice = bitcoinAmount;

			if (inscription?.collection && !params?.forceBelowFloor) {
				try {
					const [{ data: collectionData }, { data: statData }] = await Promise.all([
						axios.get(`${API_URL}/collection/${inscription?.collection?.slug}`),
						axios.get(`${API_URL}/collection/${inscription?.collection?.slug}/stats`)
					])

					if (collectionData.creator_address) {
						creator_address = collectionData.creator_address
					}

					setFloorPrice(statData.floor_price)
					if (salePrice < statData.floor_price) {
						setLoading(false)
						setOpenBelowFloor(true)
						return
					}
				} catch (e) {
					ErrorHandler.snackbar(e)
					setLoading(false)
					return
				}
			}

			try {

				const marketFee = Math.max(Math.floor(salePrice * 0.027), 1000)
				const creatorFee = creator_address ? Math.floor(salePrice * 0.042 / (1 - 0.042)) : 0
				const escrowPrice = salePrice - marketFee - creatorFee

				const handleError = (e: any) => {
					console.log(e)
					const errorDetail = ErrorHandler.errorDetail(e)
					if (errorDetail?.type == 'EXCESS_SATS_IN_INSCRIPTION_OUTPOINT') {
						setTotalSats(errorDetail?.totalSats)
						setOpenExcessSats(true)
					} else if (errorDetail?.type == 'MULTIPLE_INSCRIPTIONS_IN_OUTPOINT') {
						setInscriptions(errorDetail?.inscriptions)
						setOpenMultiInscriptions(true)
					} else {
						ErrorHandler.snackbar(e)
					}
					setLoading(false)
				}

				let psbt
				try {
					if (snipeProtection.enabled) {
						const secureListing = await prepareSecureListing({
							inscription,
							inscriptionId: inscription.id,
							priceSats: escrowPrice,
							protocol: protocolFromInscription(inscription)
						})
						event.emit('open-sell-modal', {
							event: 'create-listing',
							inscription,
							price_sats: salePrice,
							creator_address,
							secureListing
						})
						setLoading(false)
						return
					}
					psbt = await Wallet.getEscrow(inscription.id, escrowPrice, (params as any))
				} catch (e) {
					return handleError(e)
				}

				console.log(psbt);

				const { rawtx, success } = await Wallet.signEscrow(psbt)
				if (success) {
					event.emit('open-sell-modal', {
						event: 'create-listing',
						inscription,
						price_sats: salePrice,
						rawtx,
						creator_address
					})
				}
			} catch (e) {
				console.log('LIST_FOR_SALE_ERROR', e)
				ErrorHandler.snackbar(e)
			}

			setLoading(false)
		},
		[bitcoinAmount, inscription, loading, value?.length, snipeProtection.enabled]
	)

	const handleConfirmExcessSats = React.useCallback(() => {
		let tmpParams = { ...forceParams, forceExcessSats: true }
		setForceParams(tmpParams)
		setOpenExcessSats(false)
		handleSubmit(null, tmpParams)
	}, [handleSubmit, forceParams])

	const handleConfirmBelowFloor = React.useCallback(() => {
		let tmpParams = { ...forceParams, forceBelowFloor: true }
		setForceParams(tmpParams)
		setOpenBelowFloor(false)
		handleSubmit(null, tmpParams)
	}, [handleSubmit, forceParams])

	React.useEffect(() => {
		if (!open && !loading) {
			requestAnimationFrame(() => {
				setValue('')
				setTotalSats(0)
				setInscriptions([])
				setLoading(false)
				setForceParams((null as any))
			})
		}
	}, [open, loading])

	React.useEffect(() => {
		const handleSuccess = (payload: any) => {
			if (payload.event === 'success') {
				setOpen(false)
			}
		}

		event.listen(EVENT, handleSuccess)

		return () => {
			event.removeListener(EVENT, handleSuccess)
		}
	}, [setOpen])

	return (
		<>
			<ActionDrawer
				title="List for sale"
				icon={() => <MarketIcon className={styles.icon} />}
				open={open}
				handleDrawerToggle={handleDrawerToggle}
				className={props.className}
				buttonColor={props?.buttonColor}
			>
				<ExchangeRateInput
					amount={value}
					setAmount={setValue}
					setBitcoinAmount={setBitcoinAmount}
					floor={worthSats ? String(worthSats) : undefined}
				/>
				<SnipeProtectionRow offered={snipeProtection.offered} />
				<ButtonGradientEpic onClick={handleSubmit} size="small">
					{loading ? <Loading className={styles.loading} /> : 'List for sale'}
				</ButtonGradientEpic>
			</ActionDrawer>
			{openMultiInscriptions && (
				<InscriptionWarningMultiInscriptions
					inscriptions={inscriptions as any}
					open={openMultiInscriptions}
					onClose={() => setOpenMultiInscriptions(false)}
				/>
			)}
			{openExcessSats && (
				<InscriptionWarningExcessSats
					action="sell"
					totalSats={totalSats as any}
					open={openExcessSats}
					onClose={() => setOpenExcessSats(false)}
					onConfirm={handleConfirmExcessSats}
				/>
			)}
			{openBelowFloor && (
				<InscriptionWarningBelowFloor
					bitcoinAmount={(bitcoinAmount / 1e8).toFixed(8)}
					floorPriceSats={floorPrice as any}
					open={openBelowFloor}
					onCancel={() => setOpenBelowFloor(false)}
					onConfirm={handleConfirmBelowFloor}
				/>
			)}
		</>
	)
}

export default InscriptionActionSell
